export interface FaqListItem {
  /** Texto en negrita al inicio del item (ej. "Pan de masa madre:"). Opcional. */
  label?: string;
  text: string;
  /** Si esta presente, el texto se muestra como un enlace clicable. */
  href?: string;
}

export interface FaqParagraphBlock {
  type: 'paragraph';
  text: string;
}

export interface FaqListBlock {
  type: 'list';
  items: FaqListItem[];
}

export type FaqAnswerBlock = FaqParagraphBlock | FaqListBlock;

export interface FaqItem {
  question: string;
  /**
   * La respuesta puede ser un texto simple (string) o una lista de bloques
   * (parrafos y listas con vinetas) para respuestas mas complejas.
   */
  answer: string | FaqAnswerBlock[];
}

/** Aplana una respuesta a texto plano (usado para el JSON-LD de SEO). */
const flattenAnswer = (answer: FaqItem['answer']): string => {
  if (typeof answer === 'string') return answer;
  return answer
    .map((block) => {
      if (block.type === 'paragraph') return block.text;
      return block.items
        .map((item) => (item.label ? `${item.label} ${item.text}` : item.text))
        .join(' ');
    })
    .join(' ');
};

const normalize = (value: string | null | undefined) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

export const companyFaqs: FaqItem[] = [
  {
    question: '¿Que tipo de productos vende Ardel?',
    answer:
      'Somos un bazar: trabajamos todo lo que necesitas para tu casa. Cocina (ollas, sartenes, baterias), mesa y vajilla, organizacion, bano y limpieza, y articulos de deco.'
  },
  {
    question: '¿Como eligen los productos que venden?',
    answer:
      'Priorizamos durabilidad y buena relacion precio-calidad. Antes de sumar un articulo al catalogo verificamos materiales, terminacion y que cumpla en el uso diario.'
  },
  {
    question: '¿Los productos tienen garantia?',
    answer:
      'Si. Todos los articulos cuentan con garantia por fallas de fabricacion. Guarda el comprobante de compra para gestionarla.'
  },
  {
    question: '¿Hacen envios?',
    answer:
      'Si. Hacemos envios a domicilio y tambien podes retirar tu pedido por cualquiera de nuestros locales sin costo adicional.'
  },
  {
    question: '¿Que pasa si un producto llega dañado?',
    answer:
      'Avisanos dentro de las 48 horas de recibido y lo reemplazamos. La vajilla y el vidrio viajan con proteccion adicional, pero si algo llega mal lo resolvemos.'
  },
  {
    question: '¿Puedo comprar por mayor?',
    answer:
      'Si. Para compras por cantidad escribinos por WhatsApp y te pasamos la lista con precios mayoristas.'
  },
  {
    question: '¿Que medios de pago aceptan?',
    answer:
      'Aceptamos efectivo, transferencia, tarjetas de debito y credito, y pagos con QR. En el checkout vas a ver todas las opciones disponibles.'
  }
];

export const defaultProductFaqs: FaqItem[] = [
  {
    question: 'Como elijo la medida correcta?',
    answer: 'En la ficha de cada producto figuran las medidas y la capacidad. Si tenes dudas, escribinos por WhatsApp y te ayudamos.'
  },
  {
    question: 'Los productos tienen garantia?',
    answer: 'Si. Todos los articulos tienen garantia por fallas de fabricacion. Guarda el comprobante de compra.'
  },
  {
    question: 'Que materiales usan?',
    answer: 'Trabajamos con acero inoxidable, vidrio templado, ceramica, melamina y plasticos aptos para uso alimentario, segun el producto.'
  }
];

const cocinaFaqs: FaqItem[] = [
  {
    question: 'Las ollas y sartenes sirven para todas las hornallas?',
    answer: 'La mayoria es apta para gas y electrica. Para induccion verifica que la ficha lo indique, ya que requiere base ferromagnetica.'
  },
  {
    question: 'Se pueden lavar en lavavajillas?',
    answer: 'Depende del producto y figura en la ficha. Como regla, el acero inoxidable si; los antiadherentes duran mas lavados a mano.'
  },
  {
    question: 'Como cuido el antiadherente?',
    answer: 'Evita utensilios metalicos y el fuego muy alto sin contenido. Con utensilios de silicona o madera dura mucho mas.'
  }
];

const mesaFaqs: FaqItem[] = [
  {
    question: 'Se pueden usar en microondas?',
    answer: 'La vajilla de ceramica y vidrio templado suele ser apta. Evita siempre las piezas con detalles metalizados.'
  },
  {
    question: 'Venden piezas sueltas o solo juegos?',
    answer: 'Depende del articulo: hay juegos completos y piezas individuales para reponer lo que se rompe.'
  },
  {
    question: 'Como se embalan para el envio?',
    answer: 'La vajilla viaja con proteccion adicional. Si algo llega dañado, avisanos dentro de las 48 horas y lo resolvemos.'
  }
];

const organizacionFaqs: FaqItem[] = [
  {
    question: 'Como se cuanto entra en cada organizador?',
    answer: 'En la ficha figuran las medidas externas y la capacidad, para que puedas verificar el espacio disponible antes de comprar.'
  },
  {
    question: 'Los cestos y cajas son apilables?',
    answer: 'Muchos modelos lo son y se indica en la descripcion. Los apilables permiten aprovechar mejor la altura del mueble.'
  }
];

const banoFaqs: FaqItem[] = [
  {
    question: 'Los accesorios necesitan perforar la pared?',
    answer: 'Depende del modelo: hay de adherir y de atornillar. La ficha indica el sistema de fijacion de cada uno.'
  },
  {
    question: 'El acero se oxida en ambientes humedos?',
    answer: 'El acero inoxidable esta preparado para el bano. Secarlo despues de usarlo prolonga su terminacion.'
  }
];

export const getProductCategoryFaqs = (categoryName: string, subcategoryName?: string): FaqItem[] => {
  const text = normalize(`${categoryName} ${subcategoryName || ''}`);

  if (
    text.includes('coccion') ||
    text.includes('bateria') ||
    text.includes('olla') ||
    text.includes('sarten') ||
    text.includes('cacerola') ||
    text.includes('asadera') ||
    text.includes('pizzera')
  ) {
    return cocinaFaqs;
  }
  if (
    text.includes('mesa') ||
    text.includes('vajilla') ||
    text.includes('plato') ||
    text.includes('vaso') ||
    text.includes('copa') ||
    text.includes('cubierto')
  ) {
    return mesaFaqs;
  }
  if (
    text.includes('organizacion') ||
    text.includes('cesto') ||
    text.includes('caja') ||
    text.includes('canasto')
  ) {
    return organizacionFaqs;
  }
  if (
    text.includes('bano') ||
    text.includes('limpieza') ||
    text.includes('dispenser') ||
    text.includes('escobilla')
  ) {
    return banoFaqs;
  }

  if (categoryName || subcategoryName) {
    return [
      {
        question: `Que productos encuentro en ${subcategoryName || categoryName}?`,
        answer: `En ${subcategoryName || categoryName} vas a ver los productos activos del catalogo, con su precio y disponibilidad actualizados.`
      },
      {
        question: `Como se cuida la calidad de ${subcategoryName || categoryName}?`,
        answer: 'Cuidamos la seleccion, el embalaje y la entrega para que el producto llegue en perfecto estado.'
      },
      {
        question: `Puedo consultar medidas de ${subcategoryName || categoryName}?`,
        answer: 'Si. Podes consultar por WhatsApp o desde la tienda para conocer medidas, materiales y disponibilidad.'
      }
    ];
  }

  return defaultProductFaqs;
};

export const toFaqPageJsonLd = (faqs: FaqItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: flattenAnswer(faq.answer)
    }
  }))
});
